import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const Wrap = ({ children, className = "" }) => (
  <div className={`max-w-[860px] mx-auto px-6 ${className}`}>{children}</div>
);

const TABS = [
  { id: "terms", label: "Terms of Service" },
  { id: "privacy", label: "Privacy Policy" },
];

export default function Legal() {
  const initial = typeof window !== "undefined" && window.location.hash === "#privacy" ? "privacy" : "terms";
  const [tab, setTab] = useState(initial);

  return (
    <div className="min-h-screen" style={{ background: "var(--cream)" }} data-testid="legal-root">
      {/* Nav */}
      <header className="border-b-2" style={{ borderColor: "var(--ink)" }}>
        <div className="max-w-[1180px] mx-auto px-6 flex justify-between items-center py-4">
          <Link to="/" className="text-2xl font-display font-black" data-testid="legal-brand">
            Ask<span style={{ color: "var(--kiwi)" }}>Tax</span>
          </Link>
          <Link to="/" className="text-sm font-display font-bold uppercase tracking-wide flex items-center gap-2" data-testid="legal-back">
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 border-b-2" style={{ borderColor: "var(--ink)", background: "var(--cream-2)" }}>
        <Wrap>
          <span className="eyebrow">Legal</span>
          <h1 className="h1-display mt-3" style={{ fontSize: "clamp(44px,7vw,80px)" }}>Terms &amp; Privacy</h1>
          <p className="mt-4 font-serif italic text-lg" style={{ color: "var(--muted)" }}>
            Last updated: 27 July 2026 · These apply to everyone who uses asktax.co.nz.
          </p>
          <div className="mt-8 vtoggle">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); if (typeof window !== "undefined") window.location.hash = t.id; }}
                className={`vt-btn ${tab === t.id ? "on" : ""}`}
                data-testid={`legal-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Wrap>
      </section>

      {/* Body */}
      <section className="py-14">
        <Wrap>
          {tab === "terms" ? <Terms /> : <Privacy />}
        </Wrap>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t-2" style={{ borderColor: "var(--ink)", background: "var(--ink)", color: "var(--cream-3)" }}>
        <div className="max-w-[1180px] mx-auto px-6 flex flex-wrap justify-between gap-4 text-sm">
          <div className="font-display font-black text-lg">Ask<span style={{ color: "var(--kiwi-2)" }}>Tax</span></div>
          <div>© 2026 AskTax · asktax.co.nz</div>
          <div>IRD-registered New Zealand tax agent</div>
        </div>
      </footer>
    </div>
  );
}

const H2 = ({ n, children }) => (
  <h2 className="font-display font-black uppercase tracking-tight text-2xl mt-12 mb-5 flex items-baseline gap-3" style={{ letterSpacing: "-0.01em" }}>
    <span style={{ color: "var(--kiwi)" }}>{n}.</span>
    <span>{children}</span>
  </h2>
);

const P = ({ children, lead }) => (
  <p className={`font-serif ${lead ? "text-lg" : "text-base"} mt-4 leading-relaxed`} style={{ color: lead ? "var(--ink)" : "var(--ink-2)" }}>
    {children}
  </p>
);

const UL = ({ items }) => (
  <ul className="mt-4 space-y-3 pl-6" style={{ listStyle: "square", color: "var(--ink-2)" }}>
    {items.map((it, i) => <li key={i} className="font-serif leading-relaxed">{it}</li>)}
  </ul>
);

const Callout = ({ children }) => (
  <div className="my-6 p-6 border-2 rounded" style={{ borderColor: "var(--ink)", background: "var(--cream-3)", borderLeft: "6px solid var(--kiwi)" }}>
    <div className="font-serif text-base leading-relaxed" style={{ color: "var(--ink)" }}>{children}</div>
  </div>
);

function Terms() {
  return (
    <div data-testid="legal-terms">
      <Callout>
        <b>The short version.</b> AskTax gives you a written answer to one specific New Zealand tax question, for a flat fee of $67, written by a registered tax agent. It's general guidance based on what you tell us — not a full review of your circumstances, and not something you should treat as complete formal advice for a specific transaction. Nothing here takes away the rights you have under New Zealand consumer law.
      </Callout>

      <H2 n="1">Who we are</H2>
      <P>AskTax ("<strong>AskTax</strong>", "<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>") is a tax question-and-answer service operated by <strong>Jiten Joshi &amp; Associates Limited</strong>, a New Zealand registered company and IRD-registered tax agent, trading as AskTax, contactable at <strong>help@asktax.co.nz</strong>. In these Terms, "you" means the person using the service.</P>
      <P>By submitting a question and payment through asktax.co.nz (the "<strong>Site</strong>"), you agree to these Terms of Service.</P>

      <H2 n="2">What the service is</H2>
      <P lead>AskTax answers one specific tax question at a time.</P>
      <P>For a flat fee of <strong>$67 (GST inclusive)</strong> per question, we provide a written response to a single New Zealand tax question, based only on the information you give us. A qualified, IRD-registered tax agent reviews and writes every answer.</P>
      <P>Our aim is to send your answer <strong>within one business day</strong> of receiving your question and payment. This is a target, not a guarantee — if we need more information from you, or your question is more involved than it first appeared, it may take longer, and we'll tell you.</P>
      <P>Each purchase covers <strong>one question on one topic</strong>, and entitles you to one written answer to that question. If what you send is really several separate questions, we'll let you know before doing any further work, so you can decide whether to purchase additional answers. If you have a further or follow-up question after receiving your answer, that's simply a new question you're welcome to ask at any time.</P>
      <P>You may also purchase a <strong>3-question pack for $127 (GST inclusive)</strong>, which entitles you to three written answers to three separate questions — that's $42.33 a question. The three questions are held in your account as <strong>prepaid credits</strong>, do not expire, and may be used at any time on any topics. Each credit entitles you to one written answer to one question on one topic, on the same terms as a single question.</P>

      <H2 n="3">What the service is <em className="font-serif-i" style={{ color: "var(--kiwi)" }}>not</em></H2>
      <P>To make sure expectations are clear:</P>
      <UL items={[
        <>Our answer is <strong>general guidance</strong> based on the facts you provide. It is <strong>not</strong> a review of your full financial or tax position, and we don't review your accounts, documents, or IRD records unless we separately agree to.</>,
        <>It is <strong>not</strong> a formal written tax opinion, an audit, a tax return, or representation before Inland Revenue.</>,
        <>It should <strong>not</strong> be relied on as the sole basis for a significant financial decision or transaction. Tax outcomes depend heavily on your specific circumstances, and small differences in facts can change the answer.</>,
        <>We do <strong>not</strong> provide legal advice, financial product advice, or investment advice.</>,
      ]} />
      <P>If your situation needs a full review, we'll tell you honestly and can offer you a separate, fuller engagement. Where we do, we may credit your $67 toward that engagement.</P>

      <H2 n="4">Accuracy and your responsibility</H2>
      <P>Our answer is only as good as the information you give us. You agree that:</P>
      <UL items={[
        <>The facts you provide are true, accurate, and complete to the best of your knowledge.</>,
        <>You understand that if any relevant fact is left out or wrong, the answer may not apply to your situation.</>,
        <>Tax law changes, and an answer is based on the law and IRD practice as we understand it at the time we write it.</>,
        <>For anything important, you should confirm the position with a full engagement before acting on it.</>,
      ]} />

      <H2 n="5">Payment</H2>
      <P>Payment is made at the time you submit your question, through our third-party payment provider, <strong>Stripe</strong> — either $67 (GST inclusive) for a single question, or $127 (GST inclusive) for a 3-question pack. We don't see or store your full card details — Stripe handles that. Your purchase is complete when payment is confirmed.</P>

      <H2 n="6">Refunds and your consumer rights</H2>
      <P lead>Your rights under the Consumer Guarantees Act 1993 and Fair Trading Act 1986 always apply, and nothing in these Terms limits them.</P>
      <P>Under the Consumer Guarantees Act, our service must be carried out with reasonable care and skill and be fit for purpose. If it isn't, you're entitled to a remedy under that Act.</P>
      <P>On top of those rights, our own policy is simple:</P>
      <UL items={[
        <>If we're <strong>unable to answer</strong> your question, we'll refund your $67 in full.</>,
        <>If we make a genuine error in our answer, tell us — we'll correct it, and if we can't put it right, we'll refund you.</>,
        <>Because each answer is prepared specifically for you, we generally <strong>can't refund simply because the answer wasn't the outcome you were hoping for</strong> — a correct answer is still the service you paid for.</>,
        <>For a <strong>3-question pack</strong>, refunds apply <strong>per unused credit</strong>. Questions we've already answered correctly aren't refundable, but any <strong>unused credits</strong> can be refunded on request within 12 months of purchase.</>,
      ]} />
      <P>If you're not happy, contact us first at <strong>help@asktax.co.nz</strong> and we'll work with you to put it right.</P>

      <H2 n="7">Limitation of liability</H2>
      <P>This section applies <strong>except where the Consumer Guarantees Act, Fair Trading Act, or other New Zealand law says it can't</strong> — your statutory consumer rights are not affected by anything below.</P>
      <P>To the maximum extent permitted by law:</P>
      <UL items={[
        <>Our total liability to you arising from or in connection with any question and answer is limited to <strong>the amount you paid for that answer</strong> (i.e. $67 for a single question, or $42.33 per question within a 3-question pack).</>,
        <>We are not liable for indirect or consequential loss, or for loss arising because you acted on general guidance without obtaining a full review of your circumstances.</>,
        <>We are not liable for outcomes caused by information you didn't provide, provided inaccurately, or by changes in the law after your answer was given.</>,
      ]} />
      <P>If you are using AskTax for the purposes of a business, you agree that (to the extent permitted) the Consumer Guarantees Act does not apply, and the limitations above apply in full.</P>

      <H2 n="8">Acceptable use</H2>
      <P>You agree not to use AskTax for anything unlawful, and not to ask for help with evading tax, falsifying records, or any other unlawful purpose. We may decline any question at our discretion — including questions outside our expertise or scope — and if we decline before doing the work, we'll refund you.</P>

      <H2 n="9">Confidentiality</H2>
      <P>We treat the information you share with us as confidential and use it only to answer your question and run our service, as set out in our Privacy Policy. As a registered tax agent, we're also bound by professional confidentiality obligations.</P>

      <H2 n="10">Intellectual property</H2>
      <P>The answer we send is for your own use. The Site's content and design remain our property. You're welcome to use your answer for your own purposes, but not to resell or republish it as a general tax resource.</P>

      <H2 n="11">Changes to these Terms</H2>
      <P>We may update these Terms from time to time. The version that applies to your question is the one published on the Site at the time you submit it. We'll post the "last updated" date at the top.</P>

      <H2 n="12">Governing law</H2>
      <P>These Terms are governed by New Zealand law, and the New Zealand courts have jurisdiction. You may also have access to the Disputes Tribunal for smaller claims.</P>

      <H2 n="13">Contact</H2>
      <P>Questions about these Terms? Email <strong>help@asktax.co.nz</strong>.</P>
    </div>
  );
}

function Privacy() {
  return (
    <div data-testid="legal-privacy">
      <Callout>
        <b>The short version.</b> We collect your name, email, phone, and your question so we can answer it and take payment. We use Stripe for payment and Resend (email delivery) to send you your answers and notifications. We don't sell your information. You can ask to see or correct your information, or to have it deleted, at any time.
      </Callout>

      <p className="font-serif italic mt-6" style={{ color: "var(--muted)" }}>
        This Privacy Policy explains how AskTax collects, uses, and protects your personal information, in line with the <strong>Privacy Act 2020</strong> and its Information Privacy Principles.
      </p>

      <H2 n="1">Who is collecting your information</H2>
      <P>Your information is collected by <strong>Jiten Joshi &amp; Associates Limited</strong>, trading as AskTax ("we", "us"). Our Privacy Officer can be reached at <strong>help@asktax.co.nz</strong>.</P>

      <H2 n="2">What we collect</H2>
      <P>When you use AskTax, we collect:</P>
      <UL items={[
        <><strong>Contact details</strong> — your first name, last name, email address, and phone number.</>,
        <><strong>Your question</strong> — the tax question you submit and any context or details you choose to include.</>,
        <><strong>Payment information</strong> — processed by Stripe. We receive confirmation of payment and limited transaction details, but <strong>we do not collect or store your full card number</strong>.</>,
        <><strong>Technical information</strong> — basic website analytics such as your device type, browser, and general usage of the Site.</>,
      ]} />
      <P>Please only include the information needed to answer your question. Avoid sending highly sensitive identifiers (such as your full IRD number) unless we ask for them.</P>

      <H2 n="3">Why we collect it and how we use it</H2>
      <P>We collect your information for the purpose of running the AskTax service — specifically to:</P>
      <UL items={[
        <>Answer your tax question and send you the response;</>,
        <>Take and confirm your payment;</>,
        <>Contact you if we need clarification, or about your question;</>,
        <>Keep records as required for our tax-agent and business obligations;</>,
        <>Improve and operate the Site.</>,
      ]} />
      <P>Providing your information is voluntary, but without it we can't answer your question or process your payment.</P>

      <H2 n="4">Marketing</H2>
      <P>We may occasionally email you helpful tax information or updates about AskTax. Every marketing email includes an easy way to unsubscribe, and you can opt out at any time by emailing us. We won't send you marketing if you've told us not to.</P>

      <H2 n="5">Who we share it with</H2>
      <P>We don't sell your personal information. We share it only with service providers who help us run AskTax, and only as needed:</P>
      <UL items={[
        <><strong>Stripe</strong> — to process your payment.</>,
        <><strong>Resend</strong> (our email provider) — to send you your answer and related notifications.</>,
        <><strong>Professional or legal advisors, or regulators (such as Inland Revenue)</strong> — only where we're required to by law, or as part of our professional obligations.</>,
      ]} />

      <H2 n="6">Overseas storage</H2>
      <P>Some of our service providers (for example, Stripe and Resend) may store or process information on servers outside New Zealand. Where that happens, we take reasonable steps to ensure your information is protected by comparable safeguards to those required under the Privacy Act 2020. By using AskTax, you acknowledge your information may be stored overseas by these providers.</P>

      <H2 n="7">How we keep it safe</H2>
      <P>We take reasonable steps to protect your information from loss, misuse, and unauthorised access, including using reputable service providers and limiting access to your information to those who need it. No system is perfectly secure, but we work to keep your information safe and will notify you and the Privacy Commissioner of any privacy breach likely to cause serious harm, as required by law.</P>

      <H2 n="8">How long we keep it</H2>
      <P>We keep your information only as long as we need it for the purposes above, and to meet our legal and tax record-keeping obligations (generally 7 years for tax-related records). After that, we securely delete or de-identify it.</P>

      <H2 n="9">Your rights</H2>
      <P>Under the Privacy Act 2020, you have the right to:</P>
      <UL items={[
        <><strong>Access</strong> the personal information we hold about you;</>,
        <><strong>Correct</strong> it if it's wrong;</>,
        <>Ask us to <strong>delete</strong> it, where we're not required to keep it.</>,
      ]} />
      <P>To make a request, email <strong>help@asktax.co.nz</strong>. We'll respond as soon as we can, and within the timeframes the Act requires.</P>

      <H2 n="10">Cookies</H2>
      <P>Our Site uses only essential cookies needed to keep you signed in and to make the service work. We don't use advertising or third-party tracking cookies. You can control cookies through your browser settings.</P>

      <H2 n="11">Complaints</H2>
      <P>If you're concerned about how we've handled your information, please contact us first at <strong>help@asktax.co.nz</strong> so we can try to resolve it. You also have the right to complain to the <strong>Office of the Privacy Commissioner</strong> (privacy.org.nz).</P>

      <H2 n="12">Changes to this policy</H2>
      <P>We may update this Privacy Policy from time to time. The current version is always the one published here, with the "last updated" date shown at the top.</P>
    </div>
  );
}
